import Member from 'iam/models/Member'
import { CancellationToken } from 'infra/cancellation'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Observable, Subject } from 'rxjs'
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { fromStream, toStream } from 'utils/mobx-util'
import { filterNonNil, shareLatest } from 'utils/rx-util'
import { TeamSelected, TeamSelection } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepFactory, StepViewModel } from './OutlineViewModel'
import { IamPort } from './ports'

const TITLE = 'Who would you like to submit this work request for?'

const ADMIN_TITLE = 'Should this job be owned by a team?'

/**
 * Team Selector view model, for selecting the team
 * to submit a work request for.
 */
export default class TeamSelectorViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly changesSubject = new Subject<TeamSelected>()

  /**
   * Changes stream.
   */
  readonly changes$ = this.changesSubject.asObservable()

  /**
   * The selected team.
   */
  @observable _selectedTeam: TeamSelection | null = null

  /**
   * Emits the selected Team (`TeamSelection.myself` included) on subscribe,
   * but only after `prepare` has been run.
   */
  readonly selectedTeam$ = toStream(() => this.selectedTeam, true).pipe(
    filterNonNil(),
    distinctUntilChanged(),
    shareLatest(),
    this.whileReady(),
  )

  /**
   * Streams as MobX Observables.
   */
  private $ = {
    member: fromStream(Member.none, this.member$),
  }

  /**
   * Constructor.
   *
   * @param isActive$ Observable for whether the slice is active.
   * @param createStep Factory for creating a Step.
   * @param member$ Observable for the selected workspace member
   * @param adapter The IAM adapter.
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private member$: Observable<Member | null>,
    private adapter: IamPort,
    private _isDisabled: () => boolean,
    private _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    super(isActive$)
    makeObservable(this)

    // Fetch user teams on WS change; select the initial
    // team after that happens.
    this.member$
      .pipe(
        switchMap(this.fetchUserTeams),
        tap(this.selectInitialTeam),
        this.whileReady(),
      )
      .subscribe()

    this.step = createStep({
      stepId: 'team',
      isValid: () => Boolean(this._selectedTeam),
      outlineItemDisplay: () => {
        const title = this._canEditDraftJobsAsAdmin() ? ADMIN_TITLE : TITLE

        if (!this.selectedTeam) {
          return { title }
        }

        if (this.selectedTeam === TeamSelection.myself) {
          if (!this.workspaceMember) {
            return { title }
          }

          return {
            title,
            value: this.nullSelectionPlaceholder,
            visual: {
              type: 'Image',
              url: this.workspaceMember.avatar,
            },
          }
        }

        return {
          title,
          value: this.selectedTeam.name,
          visual: {
            type: 'Image',
            url: this.selectedTeam.avatar,
          },
        }
      },
    })
  }

  @computed
  get nullSelectionPlaceholder() {
    return this._canEditDraftJobsAsAdmin() ? 'None' : 'Myself'
  }

  @computed
  get canEditDraftJobsAsAdmin() {
    return this._canEditDraftJobsAsAdmin()
  }

  /**
   * This is the only step container.
   */
  @computed
  get stepContainers() {
    return this.availableTeams.length > 1 ? [this] : []
  }

  /**
   * The selected workspace member.
   */
  @computed
  get workspaceMember() {
    return this.$.member()
  }

  /**
   * The currently selected team.
   * Ensures we don't select a team that isn't in the workspace being used.
   * IMPORTANT: This will return `null` if the team members have not
   * been fetched yet.
   */
  @computed
  get selectedTeam(): TeamSelection | null {
    const team = this._selectedTeam
    if (!team) {
      return null
    }

    if (this.availableTeams.includes(team)) {
      return team
    }

    return null
  }

  /**
   * Available teams.
   */
  @computed
  get availableTeams(): TeamSelection[] {
    const member = this.workspaceMember
    if (!member) {
      return [TeamSelection.myself]
    }

    const availableTeamMembersForWorkspace = this.adapter
      .getAvailableTeamMembersByUserAndWorkspace(
        member.user.id,
        member.workspace.id,
      )
      .map((tm) => tm.team)

    if (member.hasSomePermissions('JOB_CREATE', 'ADMIN_JOB_CREATE')) {
      return [TeamSelection.myself, ...availableTeamMembersForWorkspace]
    }

    return availableTeamMembersForWorkspace
  }

  /**
   * Whether the team selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Hydrates the Team Selector state from the Job.
   *
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    await ct.race(this.fetchUserTeams())
    ct.throwIfCancelled()
    if (context.type === 'NewDraft') {
      const prefilledTeamId = context.prefilledDraft.teamId
      if (prefilledTeamId === null) {
        // If `null`, it means we explicitly set it to be `myself`
        this.selectTeam(TeamSelection.myself)
      } else if (typeof prefilledTeamId === 'string') {
        // If it's a string then its an explicit team ID.
        this.selectTeam(this.adapter.getTeam(prefilledTeamId))
      }

      // If we haven't made a selection by now, use
      // the default behavior.
      this.selectInitialTeam()
    } else {
      this.setTeam(context.job.ownerTeam ?? TeamSelection.myself)
    }
  }

  /**
   * Sets the selected team.
   *
   * @param team
   */
  @action.bound
  setTeam(team: TeamSelection | null) {
    this._selectedTeam = team ?? null
  }

  /**
   * Fetches the user's teams in the selected workspace.
   * There is no paging.
   */
  fetchUserTeams = task.resolved(async () => {
    const member = this.workspaceMember

    if (!member?.workspace.id) {
      return
    }

    await this.adapter.fetchUserTeamsByUserPublicId(
      Id.assert(member.workspace.id),
      member.user.publicId,
    )
  })

  /**
   * Selects the specified team.
   *
   * @param team
   */
  @action.bound
  selectTeam(team: TeamSelection | undefined | null) {
    team ??= null
    if (team !== this._selectedTeam) {
      this.setTeam(team)
      this.changesSubject.next({ team, type: 'TeamSelected' })
    }
  }

  /**
   * Used to select the initial team, if any.
   * Only happens if we don't have a selection.
   *
   * @private
   */
  @action.bound
  private selectInitialTeam() {
    // Do nothing if we are read-only (a Job has already been created).
    if (this._isDisabled()) {
      return
    }

    // If we already have a valid team selected,
    // do nothing.
    if (this.selectedTeam) {
      return
    }

    this.selectTeam(TeamSelection.myself)
  }
}
